export type PlatformKind =
  | "ground"
  | "stone"
  | "log"
  | "cloud"
  | "cave"
  | "leaf"
  | "wood"
  | "sand"
  | "pier"
  | "bottlecap"
  | "coral"
  | "bubble"
  | "breakable";

export type PlatformMotion = "move-x" | "move-y" | "vanish";
export type PowerUpKind = "bubble" | "glow" | "mini" | "shield";

export interface Platform {
  id?: string;
  x: number;
  y: number;
  w: number;
  h: number;
  kind: PlatformKind;
  motion?: PlatformMotion;
  range?: number;
  speed?: number;
  phase?: number;
}

export interface Collectible {
  id: string;
  x: number;
  y: number;
  kind: "fruit" | "golden-pin" | "crown-piece" | "fizz-bubble" | "golden-glow" | "mini-slice" | "pin-shield";
  collected?: boolean;
}

export interface Hazard {
  x: number;
  y: number;
  w: number;
  h: number;
  kind: "bramble" | "shells";
}

export interface GroveCritter {
  id: string;
  x: number;
  y: number;
  minX: number;
  maxX: number;
  direction: -1 | 1;
  stunned: number;
  defeated?: boolean;
  defeatTimer?: number;
  color?: string;
  speed?: number;
  species?: "bud" | "mole" | "bird" | "crab" | "puffer";
}

export interface Spring {
  x: number;
  y: number;
  w?: number;
  strength?: number;
  kind?: "spring" | "fizz";
}

export interface Slope {
  x: number;
  y: number;
  w: number;
  h: number;
  direction: "up" | "down";
}

export interface SecretArea {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  entrance: "left" | "right";
}

export interface PowerGate {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  requires: PowerUpKind;
  label: string;
}

export interface LevelTheme {
  skyTop: string;
  skyBottom: string;
  haze: string;
  hillLight: string;
  hillDark: string;
  ground: string;
  groundDetail: string;
  grass: string;
  waterTop: string;
  waterBottom: string;
  scenery:
    | "grove"
    | "burrow"
    | "treetop"
    | "orchard"
    | "sunset"
    | "shore"
    | "fizz-falls"
    | "tidepool"
    | "boardwalk"
    | "marina"
    | "crown-vault";
}

export interface Tide {
  lowY: number;
  highY: number;
  speed: number;
  phase?: number;
}

export interface LevelDefinition {
  id: string;
  name: string;
  subtitle: string;
  objective: string;
  worldNumber: number;
  worldName: string;
  worldLevel: number;
  worldWidth: number;
  worldHeight: number;
  groundY: number;
  spawnX: number;
  spawnY?: number;
  checkpointX: number;
  respawnX: number;
  goalX: number;
  parTime: number;
  theme: LevelTheme;
  platforms: Platform[];
  collectibles: Collectible[];
  hazards: Hazard[];
  critters: GroveCritter[];
  springs: Spring[];
  slopes: Slope[];
  secrets: SecretArea[];
  powerGates?: PowerGate[];
  bonus?: boolean;
  unlockCrowns?: number;
  tide?: Tide;
  cart?: { startX: number; speed: number; kind?: "fruit-cart" | "soda-float" };
}

export interface WorldDefinition {
  number: number;
  id: string;
  name: string;
  subtitle: string;
}

const WORLD_HEIGHT = 720;
const GROUND_Y = 610;

const sunny: LevelTheme = {
  skyTop: "#42bfe8", skyBottom: "#fff1aa", haze: "#a7e7f4",
  hillLight: "#79c76b", hillDark: "#2c8a53",
  ground: "#96502d", groundDetail: "#b96838", grass: "#62c95a",
  waterTop: "#43cde5", waterBottom: "#1387c1", scenery: "grove",
};

const burrow: LevelTheme = {
  skyTop: "#284b59", skyBottom: "#c5a66d", haze: "#657d75",
  hillLight: "#5c7456", hillDark: "#344e43",
  ground: "#5d4738", groundDetail: "#7b5e46", grass: "#80a45b",
  waterTop: "#45a9aa", waterBottom: "#285c77", scenery: "burrow",
};

const treetop: LevelTheme = {
  skyTop: "#4f8ee8", skyBottom: "#ffe6a2", haze: "#bce8f6",
  hillLight: "#69b87a", hillDark: "#287b5c",
  ground: "#6e482c", groundDetail: "#97623a", grass: "#65c96b",
  waterTop: "#6cd3e4", waterBottom: "#2f91bd", scenery: "treetop",
};

const orchard: LevelTheme = {
  skyTop: "#35b7dc", skyBottom: "#ffd17a", haze: "#b5e7df",
  hillLight: "#83c95c", hillDark: "#368f4d",
  ground: "#8a4a2c", groundDetail: "#bb6736", grass: "#77d44f",
  waterTop: "#54c7d8", waterBottom: "#257fa9", scenery: "orchard",
};

const sunset: LevelTheme = {
  skyTop: "#6652b7", skyBottom: "#ffbd67", haze: "#d799ad",
  hillLight: "#668a62", hillDark: "#355c55",
  ground: "#76432c", groundDetail: "#a65c35", grass: "#6bb951",
  waterTop: "#6c91c9", waterBottom: "#3e4f8f", scenery: "sunset",
};

const lemonade: LevelTheme = {
  skyTop: "#44c8ef", skyBottom: "#fff1a8", haze: "#b9edf4",
  hillLight: "#f3cf71", hillDark: "#d5a956",
  ground: "#c99149", groundDetail: "#e6b866", grass: "#f5df8b",
  waterTop: "#59ddec", waterBottom: "#168fc5", scenery: "shore",
};

const fizzFalls: LevelTheme = {
  skyTop: "#48aee8", skyBottom: "#d7f8e8", haze: "#a8e6e5",
  hillLight: "#78c8b0", hillDark: "#3c8c83",
  ground: "#af7e50", groundDetail: "#d29f68", grass: "#f0d77f",
  waterTop: "#79edf1", waterBottom: "#287fc2", scenery: "fizz-falls",
};

const tidepool: LevelTheme = {
  skyTop: "#4a9ee0", skyBottom: "#ffd2a0", haze: "#a8d9df",
  hillLight: "#a99673", hillDark: "#596f73",
  ground: "#8b684e", groundDetail: "#b48b62", grass: "#e5c77d",
  waterTop: "#45c8ce", waterBottom: "#186b9c", scenery: "tidepool",
};

const boardwalk: LevelTheme = {
  skyTop: "#2faee5", skyBottom: "#ffe6a3", haze: "#b9eaf0",
  hillLight: "#ddba75", hillDark: "#a97d57",
  ground: "#a56d45", groundDetail: "#cb915c", grass: "#f3d483",
  waterTop: "#4dd4df", waterBottom: "#1b75ad", scenery: "boardwalk",
};

const marina: LevelTheme = {
  skyTop: "#5548a7", skyBottom: "#ffac78", haze: "#c18ba6",
  hillLight: "#5b7880", hillDark: "#344d61",
  ground: "#725545", groundDetail: "#9f7557", grass: "#d5b96d",
  waterTop: "#637cc1", waterBottom: "#243b78", scenery: "marina",
};

const crownVault: LevelTheme = {
  skyTop: "#34256f", skyBottom: "#f5a850", haze: "#a978b5",
  hillLight: "#7e638d", hillDark: "#423b68",
  ground: "#6f4a35", groundDetail: "#9c6b43", grass: "#e8b83f",
  waterTop: "#7859b4", waterBottom: "#33265f", scenery: "crown-vault",
};

function ground(x: number, w: number): Platform {
  return { x, y: GROUND_Y, w, h: 160, kind: "ground" };
}

function fruits(prefix: string, x: number, y: number, count: number, step = 72): Collectible[] {
  return Array.from({ length: count }, (_, index) => ({
    id: `${prefix}-${index + 1}`,
    x: x + index * step,
    y,
    kind: "fruit" as const,
  }));
}

const groveGateway: LevelDefinition = {
  id: "grove-gateway",
  name: "Grove Gateway",
  subtitle: "A bright beginning",
  objective: "Follow the fruit trail and find the high paths.",
  worldNumber: 1,
  worldName: "Sunny Grove",
  worldLevel: 1,
  worldWidth: 6200,
  worldHeight: WORLD_HEIGHT,
  groundY: GROUND_Y,
  spawnX: 130,
  checkpointX: 3010,
  respawnX: 2880,
  goalX: 6000,
  parTime: 92,
  theme: sunny,
  platforms: [
    ground(-200, 1110), ground(1040, 700), ground(1850, 850), ground(2800, 760),
    ground(3700, 800), ground(4620, 780), ground(5520, 900),
    { x: 300, y: 500, w: 180, h: 34, kind: "log" },
    { x: 590, y: 410, w: 190, h: 40, kind: "stone" },
    { x: 840, y: 505, w: 120, h: 30, kind: "cloud" },
    { x: 1130, y: 500, w: 220, h: 34, kind: "log" },
    { x: 1440, y: 405, w: 190, h: 40, kind: "stone" },
    { x: 2010, y: 500, w: 230, h: 34, kind: "log" },
    { x: 2310, y: 405, w: 210, h: 40, kind: "stone" },
    { x: 2880, y: 500, w: 220, h: 34, kind: "log" },
    { x: 3170, y: 405, w: 190, h: 40, kind: "stone" },
    { x: 3820, y: 500, w: 230, h: 34, kind: "log" },
    { x: 4150, y: 405, w: 210, h: 40, kind: "stone" },
    { x: 4750, y: 500, w: 200, h: 34, kind: "log" },
    { x: 5030, y: 405, w: 220, h: 40, kind: "stone" },
    { id: "gateway-mover", x: 5320, y: 500, w: 130, h: 30, kind: "cloud", motion: "move-y", range: 55, speed: 1.35 },
    { x: 5660, y: 500, w: 210, h: 34, kind: "log" },
  ],
  collectibles: [
    ...fruits("g1-a", 355, 445, 5, 55),
    { id: "g1-pin-1", x: 755, y: 335, kind: "golden-pin" },
    ...fruits("g1-b", 1160, 445, 5, 60),
    { id: "g1-shield", x: 1080, y: 565, kind: "pin-shield" },
    { id: "g1-berry", x: 2180, y: 445, kind: "fizz-bubble" },
    { id: "g1-pin-2", x: 2460, y: 340, kind: "golden-pin" },
    ...fruits("g1-c-low", 2900, 445, 4, 70),
    ...fruits("g1-c-high", 3215, 350, 3, 60),
    { id: "g1-crown", x: 4230, y: 345, kind: "crown-piece" },
    ...fruits("g1-d-low", 4740, 445, 4, 82),
    ...fruits("g1-d-high", 5060, 350, 3, 75),
    { id: "g1-pin-3", x: 5125, y: 345, kind: "golden-pin" },
    ...fruits("g1-e", 5600, 445, 6, 74),
  ],
  hazards: [
    { x: 1365, y: 580, w: 64, h: 30, kind: "bramble" },
    { x: 2530, y: 580, w: 70, h: 30, kind: "bramble" },
    { x: 4060, y: 580, w: 72, h: 30, kind: "bramble" },
    { x: 5200, y: 580, w: 74, h: 30, kind: "bramble" },
  ],
  critters: [
    { id: "g1-bud-1", x: 1560, y: GROUND_Y, minX: 1490, maxX: 1680, direction: 1, stunned: 0 },
    { id: "g1-bud-2", x: 3350, y: GROUND_Y, minX: 3280, maxX: 3500, direction: -1, stunned: 0 },
    { id: "g1-bud-3", x: 5060, y: GROUND_Y, minX: 5020, maxX: 5135, direction: 1, stunned: 0 },
  ],
  springs: [{ x: 5435, y: 582, strength: 1040 }],
  slopes: [{ x: 3440, y: 520, w: 120, h: 90, direction: "down" }],
  secrets: [],
  powerGates: [
    { id: "gateway-bubble-route", x: 4092, y: 302, w: 28, h: 103, requires: "bubble", label: "FIZZ" },
  ],
};

const burrowAndBloom: LevelDefinition = {
  id: "burrow-and-bloom",
  name: "Burrow & Bloom",
  subtitle: "Secrets under the roots",
  objective: "Use Mini Slice to squeeze through the hidden root tunnel.",
  worldNumber: 1,
  worldName: "Sunny Grove",
  worldLevel: 2,
  worldWidth: 5800,
  worldHeight: WORLD_HEIGHT,
  groundY: GROUND_Y,
  spawnX: 130,
  checkpointX: 2860,
  respawnX: 2720,
  goalX: 5590,
  parTime: 100,
  theme: burrow,
  platforms: [
    ground(-200, 1320), ground(1250, 980), ground(2360, 870), ground(3370, 780), ground(4300, 1700),
    { x: 300, y: 505, w: 150, h: 34, kind: "log" },
    { x: 520, y: 455, w: 600, h: 95, kind: "cave" },
    { x: 650, y: 375, w: 180, h: 34, kind: "stone" },
    { x: 1260, y: 500, w: 105, h: 28, kind: "log" },
    { id: "secret-root-ceiling", x: 1430, y: 455, w: 520, h: 68, kind: "cave" },
    { x: 1460, y: 355, w: 180, h: 34, kind: "stone" },
    { x: 1770, y: 355, w: 180, h: 34, kind: "stone" },
    { id: "burrow-lift", x: 2180, y: 500, w: 150, h: 30, kind: "stone", motion: "move-y", range: 90, speed: 1.25, phase: 0.8 },
    { x: 2440, y: 500, w: 220, h: 34, kind: "log" },
    { x: 2740, y: 405, w: 210, h: 38, kind: "stone" },
    { x: 3070, y: 500, w: 130, h: 32, kind: "cloud" },
    { id: "burrow-slider", x: 3260, y: 420, w: 150, h: 32, kind: "stone", motion: "move-x", range: 95, speed: 1.1 },
    { x: 3540, y: 500, w: 180, h: 36, kind: "cave" },
    { id: "break-a", x: 3800, y: 438, w: 72, h: 72, kind: "breakable" },
    { id: "break-b", x: 3872, y: 438, w: 72, h: 72, kind: "breakable" },
    { x: 4050, y: 405, w: 160, h: 38, kind: "stone" },
    { x: 4440, y: 500, w: 220, h: 34, kind: "log" },
    { id: "burrow-fade-1", x: 4760, y: 425, w: 140, h: 30, kind: "cloud", motion: "vanish", phase: 0 },
    { id: "burrow-fade-2", x: 4990, y: 370, w: 140, h: 30, kind: "cloud", motion: "vanish", phase: 1.1 },
    { x: 5240, y: 500, w: 190, h: 34, kind: "log" },
  ],
  collectibles: [
    { id: "g2-mini", x: 350, y: 455, kind: "mini-slice" },
    ...fruits("g2-entry-tunnel", 585, 580, 6, 82),
    { id: "g2-pin-1", x: 750, y: 315, kind: "golden-pin" },
    ...fruits("g2-secret-tunnel", 1510, 575, 5, 78),
    { id: "g2-crown", x: 1870, y: 575, kind: "crown-piece" },
    { id: "g2-pin-2", x: 1850, y: 300, kind: "golden-pin" },
    ...fruits("g2-b", 2460, 385, 7, 80),
    { id: "g2-berry", x: 2830, y: 345, kind: "fizz-bubble" },
    ...fruits("g2-c-low", 3550, 445, 3, 70),
    ...fruits("g2-c-high", 3990, 365, 3, 65),
    { id: "g2-pin-3", x: 4120, y: 345, kind: "golden-pin" },
    ...fruits("g2-d-low", 4470, 445, 4, 75),
    ...fruits("g2-d-high", 4800, 350, 4, 75),
  ],
  hazards: [
    { x: 1080, y: 580, w: 55, h: 30, kind: "bramble" },
    { x: 2550, y: 580, w: 72, h: 30, kind: "bramble" },
    { x: 3610, y: 580, w: 72, h: 30, kind: "bramble" },
    { x: 4580, y: 580, w: 76, h: 30, kind: "bramble" },
  ],
  critters: [
    { id: "g2-mole-1", x: 2050, y: GROUND_Y, minX: 1990, maxX: 2100, direction: 1, stunned: 0, color: "#b38a5b", speed: 46, species: "mole" },
    { id: "g2-mole-2", x: 2850, y: GROUND_Y, minX: 2780, maxX: 2990, direction: -1, stunned: 0, color: "#b38a5b", speed: 48, species: "mole" },
    { id: "g2-mole-3", x: 4890, y: GROUND_Y, minX: 4820, maxX: 5110, direction: 1, stunned: 0, color: "#b38a5b", speed: 50, species: "mole" },
  ],
  springs: [{ x: 4150, y: 582, strength: 1080 }],
  slopes: [],
  secrets: [
    { id: "root-crown-chamber", x: 1430, y: 455, w: 520, h: 155, entrance: "left" },
  ],
};

const treetopPicnic: LevelDefinition = {
  id: "treetop-picnic",
  name: "Treetop Picnic",
  subtitle: "Above the golden canopy",
  objective: "Grab Golden Glow and race along the highest treetop route.",
  worldNumber: 1,
  worldName: "Sunny Grove",
  worldLevel: 3,
  worldWidth: 5750,
  worldHeight: WORLD_HEIGHT,
  groundY: GROUND_Y,
  spawnX: 130,
  checkpointX: 2820,
  respawnX: 2720,
  goalX: 5530,
  parTime: 98,
  theme: treetop,
  platforms: [
    ground(-200, 780), ground(900, 500), ground(1700, 420), ground(2480, 420),
    ground(3320, 430), ground(4190, 440), ground(5060, 900),
    { x: 330, y: 380, w: 230, h: 40, kind: "leaf" },
    { x: 640, y: 465, w: 180, h: 34, kind: "wood" },
    { x: 850, y: 375, w: 170, h: 38, kind: "leaf" },
    { id: "tree-lift-1", x: 1120, y: 500, w: 135, h: 30, kind: "cloud", motion: "move-y", range: 85, speed: 1.15 },
    { x: 1370, y: 405, w: 210, h: 38, kind: "leaf" },
    { id: "tree-drift-1", x: 1600, y: 325, w: 150, h: 30, kind: "cloud", motion: "move-x", range: 90, speed: 1.0 },
    { x: 1860, y: 500, w: 190, h: 34, kind: "wood" },
    { x: 2150, y: 405, w: 190, h: 38, kind: "leaf" },
    { id: "tree-fade-1", x: 2410, y: 325, w: 145, h: 30, kind: "cloud", motion: "vanish", phase: 0.4 },
    { x: 2680, y: 465, w: 210, h: 38, kind: "leaf" },
    { id: "tree-lift-2", x: 3010, y: 500, w: 140, h: 30, kind: "wood", motion: "move-y", range: 90, speed: 1.35, phase: 1.2 },
    { x: 3370, y: 405, w: 210, h: 38, kind: "leaf" },
    { id: "tree-drift-2", x: 3660, y: 325, w: 150, h: 30, kind: "cloud", motion: "move-x", range: 100, speed: 1.2, phase: 0.7 },
    { x: 3970, y: 500, w: 200, h: 34, kind: "wood" },
    { x: 4330, y: 405, w: 210, h: 38, kind: "leaf" },
    { id: "tree-fade-2", x: 4620, y: 325, w: 145, h: 30, kind: "cloud", motion: "vanish", phase: 1.6 },
    { x: 4840, y: 500, w: 185, h: 34, kind: "wood" },
    { x: 5210, y: 405, w: 200, h: 38, kind: "leaf" },
  ],
  collectibles: [
    { id: "g3-a-1", x: 270, y: 520, kind: "fruit" },
    { id: "g3-a-2", x: 310, y: 460, kind: "fruit" },
    { id: "g3-a-3", x: 350, y: 350, kind: "fruit" },
    { id: "g3-a-4", x: 405, y: 300, kind: "fruit" },
    { id: "g3-a-5", x: 475, y: 290, kind: "fruit" },
    { id: "g3-crown-power", x: 3400, y: 345, kind: "golden-glow" },
    { id: "g3-pin-1", x: 930, y: 315, kind: "golden-pin" },
    ...fruits("g3-b-low", 1380, 345, 2, 70),
    ...fruits("g3-b-high", 1530, 265, 5, 70),
    { id: "g3-pin-2", x: 2750, y: 400, kind: "golden-pin" },
    ...fruits("g3-c-low", 2700, 400, 4, 80),
    ...fruits("g3-c-high", 3100, 345, 3, 80),
    { id: "g3-crown", x: 3525, y: 345, kind: "crown-piece" },
    { id: "g3-berry", x: 3700, y: 265, kind: "fizz-bubble" },
    ...fruits("g3-d", 3990, 360, 7, 82),
    { id: "g3-pin-3", x: 4700, y: 265, kind: "golden-pin" },
    ...fruits("g3-e", 4870, 345, 7, 78),
  ],
  hazards: [
    { x: 1070, y: 580, w: 70, h: 30, kind: "bramble" },
    { x: 2610, y: 580, w: 70, h: 30, kind: "bramble" },
    { x: 4270, y: 580, w: 72, h: 30, kind: "bramble" },
  ],
  critters: [
    { id: "g3-bird-1", x: 1770, y: GROUND_Y, minX: 1740, maxX: 1800, direction: 1, stunned: 0, color: "#e5a94f", speed: 62, species: "bird" },
    { id: "g3-bird-2", x: 3400, y: GROUND_Y, minX: 3370, maxX: 3670, direction: -1, stunned: 0, color: "#e5a94f", speed: 66, species: "bird" },
    { id: "g3-bird-3", x: 5180, y: GROUND_Y, minX: 5120, maxX: 5430, direction: 1, stunned: 0, color: "#e5a94f", speed: 64, species: "bird" },
  ],
  springs: [
    { x: 275, y: 582, strength: 1130 },
    { x: 3220, y: 582, strength: 1120 },
  ],
  slopes: [],
  secrets: [],
  powerGates: [
    { id: "treetop-glow-route", x: 3470, y: 285, w: 28, h: 120, requires: "glow", label: "GLOW" },
  ],
};

const orchardRush: LevelDefinition = {
  id: "orchard-rush",
  name: "Orchard Rush",
  subtitle: "Roll with the hills",
  objective: "Use slopes and springs to beat the orchard’s par time.",
  worldNumber: 1,
  worldName: "Sunny Grove",
  worldLevel: 4,
  worldWidth: 5900,
  worldHeight: WORLD_HEIGHT,
  groundY: GROUND_Y,
  spawnX: 130,
  spawnY: 381,
  checkpointX: 2920,
  respawnX: 2790,
  goalX: 5680,
  parTime: 76,
  theme: orchard,
  platforms: [
    { x: -200, y: 455, w: 700, h: 315, kind: "ground" },
    ground(800, 1280), ground(2240, 760), ground(3150, 830),
    ground(4140, 760), ground(5050, 1050),
    { x: 900, y: 500, w: 190, h: 34, kind: "log" },
    { id: "rush-fade-1", x: 1180, y: 425, w: 130, h: 30, kind: "cloud", motion: "vanish", phase: 0 },
    { x: 1430, y: 500, w: 210, h: 34, kind: "log" },
    { id: "rush-drift", x: 1720, y: 405, w: 155, h: 30, kind: "cloud", motion: "move-x", range: 90, speed: 1.55 },
    { x: 1990, y: 500, w: 180, h: 38, kind: "stone" },
    { x: 2360, y: 500, w: 210, h: 34, kind: "log" },
    { id: "rush-fade-2", x: 2680, y: 410, w: 140, h: 30, kind: "cloud", motion: "vanish", phase: 1.1 },
    { x: 3060, y: 500, w: 160, h: 38, kind: "stone" },
    { x: 3420, y: 405, w: 210, h: 34, kind: "log" },
    { id: "rush-lift", x: 3790, y: 500, w: 145, h: 30, kind: "cloud", motion: "move-y", range: 80, speed: 1.55, phase: 0.4 },
    { x: 4070, y: 405, w: 210, h: 38, kind: "leaf" },
    { id: "rush-fade-3", x: 4490, y: 480, w: 145, h: 30, kind: "cloud", motion: "vanish", phase: 2.0 },
    { x: 4770, y: 405, w: 190, h: 34, kind: "log" },
    { x: 5200, y: 455, w: 210, h: 38, kind: "leaf" },
  ],
  collectibles: [
    ...fruits("g4-start", 260, 400, 4, 70),
    { id: "g4-slope-1", x: 540, y: 435, kind: "fruit" },
    { id: "g4-slope-2", x: 600, y: 465, kind: "fruit" },
    { id: "g4-slope-3", x: 660, y: 495, kind: "fruit" },
    { id: "g4-slope-4", x: 720, y: 525, kind: "fruit" },
    { id: "g4-pin-1", x: 985, y: 440, kind: "golden-pin" },
    { id: "g4-shield", x: 450, y: 400, kind: "pin-shield" },
    { id: "g4-berry", x: 1500, y: 440, kind: "fizz-bubble" },
    ...fruits("g4-b-low", 1360, 445, 4, 78),
    ...fruits("g4-b-high", 1680, 350, 4, 78),
    { id: "g4-crown", x: 2760, y: 350, kind: "crown-piece" },
    { id: "g4-pin-2", x: 3500, y: 345, kind: "golden-pin" },
    ...fruits("g4-c-low", 3070, 450, 4, 75),
    ...fruits("g4-c-high", 3450, 350, 4, 70),
    { id: "g4-crown-power", x: 4160, y: 345, kind: "golden-glow" },
    ...fruits("g4-d", 4440, 345, 9, 82),
    { id: "g4-pin-3", x: 5280, y: 395, kind: "golden-pin" },
  ],
  hazards: [
    { x: 960, y: 580, w: 68, h: 30, kind: "bramble" },
    { x: 1900, y: 580, w: 72, h: 30, kind: "bramble" },
    { x: 3320, y: 580, w: 74, h: 30, kind: "bramble" },
    { x: 4250, y: 580, w: 72, h: 30, kind: "bramble" },
  ],
  critters: [
    { id: "g4-bud-1", x: 1260, y: GROUND_Y, minX: 1210, maxX: 1370, direction: 1, stunned: 0, speed: 75 },
    { id: "g4-bud-2", x: 2700, y: GROUND_Y, minX: 2630, maxX: 2920, direction: -1, stunned: 0, speed: 78 },
    { id: "g4-bud-3", x: 4720, y: GROUND_Y, minX: 4665, maxX: 4750, direction: 1, stunned: 0, speed: 80 },
  ],
  springs: [
    { x: 1060, y: 582, strength: 1080 },
    { x: 2010, y: 582, strength: 1120 },
    { x: 4820, y: 582, strength: 1080 },
  ],
  slopes: [
    { x: 500, y: 455, w: 180, h: 155, direction: "down" },
    { x: 2850, y: 520, w: 150, h: 90, direction: "down" },
    { x: 3820, y: 520, w: 160, h: 90, direction: "up" },
  ],
  secrets: [],
  powerGates: [
    { id: "orchard-shield-route", x: 2638, y: 305, w: 28, h: 105, requires: "shield", label: "SHIELD" },
  ],
};

const fruitCartChase: LevelDefinition = {
  id: "fruit-cart-chase",
  name: "The Great Fruit-Cart Chase",
  subtitle: "A rolling Sunny Grove finale",
  objective: "Catch the runaway picnic cart before the grove gate.",
  worldNumber: 1,
  worldName: "Sunny Grove",
  worldLevel: 5,
  worldWidth: 6300,
  worldHeight: WORLD_HEIGHT,
  groundY: GROUND_Y,
  spawnX: 130,
  checkpointX: 3100,
  respawnX: 3060,
  goalX: 6060,
  parTime: 88,
  theme: sunset,
  cart: { startX: 620, speed: 135, kind: "fruit-cart" },
  platforms: [
    ground(-200, 1200), ground(1130, 830), ground(2080, 820), ground(3030, 820),
    ground(4000, 820), ground(4960, 1500),
    { x: 350, y: 500, w: 200, h: 34, kind: "log" },
    { x: 760, y: 405, w: 180, h: 38, kind: "stone" },
    { id: "cart-fade-1", x: 1030, y: 500, w: 130, h: 30, kind: "cloud", motion: "vanish", phase: 0.5 },
    { x: 1300, y: 500, w: 210, h: 34, kind: "log" },
    { id: "cart-mover-1", x: 1690, y: 405, w: 150, h: 30, kind: "cloud", motion: "move-x", range: 90, speed: 1.35 },
    { x: 2130, y: 500, w: 210, h: 38, kind: "stone" },
    { id: "cart-break-1", x: 2490, y: 438, w: 72, h: 72, kind: "breakable" },
    { id: "cart-break-2", x: 2562, y: 438, w: 72, h: 72, kind: "breakable" },
    { x: 2780, y: 500, w: 180, h: 34, kind: "log" },
    { id: "cart-lift", x: 3180, y: 500, w: 145, h: 30, kind: "cloud", motion: "move-y", range: 80, speed: 1.4 },
    { x: 3500, y: 405, w: 210, h: 38, kind: "leaf" },
    { id: "cart-fade-2", x: 3890, y: 500, w: 140, h: 30, kind: "cloud", motion: "vanish", phase: 1.4 },
    { x: 4210, y: 405, w: 210, h: 34, kind: "log" },
    { id: "cart-mover-2", x: 4590, y: 500, w: 150, h: 30, kind: "cloud", motion: "move-x", range: 95, speed: 1.5, phase: 0.8 },
    { x: 5010, y: 405, w: 220, h: 38, kind: "stone" },
    { x: 5480, y: 500, w: 210, h: 34, kind: "log" },
    { x: 5750, y: 405, w: 180, h: 38, kind: "leaf" },
  ],
  collectibles: [
    ...fruits("g5-a", 320, 445, 7, 55),
    { id: "g5-pin-1", x: 850, y: 345, kind: "golden-pin" },
    { id: "g5-crown-power", x: 1400, y: 445, kind: "golden-glow" },
    ...fruits("g5-b-low", 1320, 445, 4, 70),
    ...fruits("g5-b-high", 1700, 350, 4, 70),
    { id: "g5-crown", x: 2870, y: 345, kind: "crown-piece" },
    { id: "g5-pin-2", x: 3600, y: 345, kind: "golden-pin" },
    ...fruits("g5-c-low", 3050, 445, 2, 80),
    ...fruits("g5-c-high", 3370, 345, 6, 80),
    { id: "g5-berry", x: 4290, y: 345, kind: "fizz-bubble" },
    ...fruits("g5-d", 4300, 345, 10, 82),
    { id: "g5-pin-3", x: 5820, y: 345, kind: "golden-pin" },
  ],
  hazards: [
    { x: 930, y: 580, w: 72, h: 30, kind: "bramble" },
    { x: 1810, y: 580, w: 72, h: 30, kind: "bramble" },
    { x: 2710, y: 580, w: 72, h: 30, kind: "bramble" },
    { x: 3660, y: 580, w: 74, h: 30, kind: "bramble" },
    { x: 4650, y: 580, w: 72, h: 30, kind: "bramble" },
  ],
  critters: [
    { id: "g5-bud-1", x: 1650, y: GROUND_Y, minX: 1560, maxX: 1740, direction: 1, stunned: 0, color: "#d78f47", speed: 70 },
    { id: "g5-bud-2", x: 2500, y: GROUND_Y, minX: 2385, maxX: 2640, direction: -1, stunned: 0, color: "#d78f47", speed: 74 },
    { id: "g5-bud-3", x: 4140, y: GROUND_Y, minX: 4070, maxX: 4460, direction: 1, stunned: 0, color: "#d78f47", speed: 76 },
    { id: "g5-bud-4", x: 5200, y: GROUND_Y, minX: 5100, maxX: 5415, direction: -1, stunned: 0, color: "#d78f47", speed: 78 },
  ],
  springs: [
    { x: 1908, y: 582, strength: 1090 },
    { x: 4750, y: 582, strength: 1080 },
  ],
  slopes: [],
  secrets: [],
  powerGates: [
    { id: "cart-glow-route", x: 2735, y: 380, w: 28, h: 120, requires: "glow", label: "GLOW" },
  ],
};

const lemonadeLanding: LevelDefinition = {
  id: "lemonade-landing",
  name: "Lemonade Landing",
  subtitle: "First steps on the sparkling shore",
  objective: "Cross the bottle caps and follow the foam to the beach gate.",
  worldNumber: 2,
  worldName: "Soda Pop Shore",
  worldLevel: 1,
  worldWidth: 6000,
  worldHeight: WORLD_HEIGHT,
  groundY: GROUND_Y,
  spawnX: 130,
  checkpointX: 2910,
  respawnX: 2960,
  goalX: 5780,
  parTime: 92,
  theme: lemonade,
  tide: { lowY: 666, highY: 642, speed: 0.55 },
  platforms: [
    ground(-200, 1050), ground(1040, 760), ground(1940, 780), ground(2860, 780),
    ground(3800, 720), ground(4690, 1510),
    { x: 300, y: 500, w: 210, h: 34, kind: "sand" },
    { x: 610, y: 410, w: 185, h: 36, kind: "coral" },
    { id: "landing-cap-1", x: 865, y: 500, w: 130, h: 30, kind: "bottlecap", motion: "move-y", range: 34, speed: 1.0 },
    { x: 1160, y: 500, w: 205, h: 34, kind: "pier" },
    { x: 1450, y: 405, w: 185, h: 36, kind: "coral" },
    { id: "landing-cap-2", x: 1795, y: 500, w: 135, h: 30, kind: "bottlecap", motion: "move-y", range: 30, speed: 1.15, phase: 1.2 },
    { x: 2050, y: 500, w: 210, h: 34, kind: "pier" },
    { x: 2380, y: 405, w: 190, h: 36, kind: "coral" },
    { id: "landing-bubble", x: 2700, y: 485, w: 130, h: 30, kind: "bubble", motion: "vanish", phase: 0.4 },
    { x: 3000, y: 500, w: 220, h: 34, kind: "sand" },
    { x: 3340, y: 405, w: 190, h: 36, kind: "coral" },
    { id: "landing-cap-3", x: 3650, y: 500, w: 135, h: 30, kind: "bottlecap", motion: "move-x", range: 42, speed: 1.05 },
    { x: 3940, y: 500, w: 220, h: 34, kind: "pier" },
    { x: 4290, y: 405, w: 190, h: 36, kind: "coral" },
    { id: "landing-bubble-2", x: 4525, y: 500, w: 140, h: 30, kind: "bubble", motion: "vanish", phase: 1.5 },
    { x: 4800, y: 500, w: 220, h: 34, kind: "sand" },
    { x: 5190, y: 405, w: 200, h: 36, kind: "coral" },
    { x: 5510, y: 500, w: 210, h: 34, kind: "pier" },
  ],
  collectibles: [
    ...fruits("s1-a", 280, 565, 6, 62),
    { id: "s1-pin-1", x: 700, y: 345, kind: "golden-pin" },
    { id: "s1-shield", x: 1060, y: 565, kind: "pin-shield" },
    { id: "s1-berry", x: 1220, y: 565, kind: "fizz-bubble" },
    ...fruits("s1-b", 1150, 565, 6, 70),
    { id: "s1-pin-2", x: 2460, y: 340, kind: "golden-pin" },
    ...fruits("s1-c", 2050, 565, 7, 72),
    { id: "s1-crown", x: 3425, y: 340, kind: "crown-piece" },
    ...fruits("s1-d", 3000, 565, 6, 76),
    { id: "s1-pin-3", x: 4380, y: 340, kind: "golden-pin" },
    ...fruits("s1-e", 4780, 565, 8, 82),
  ],
  hazards: [
    { x: 1368, y: 580, w: 64, h: 30, kind: "shells" },
    { x: 2600, y: 580, w: 68, h: 30, kind: "shells" },
    { x: 4070, y: 580, w: 72, h: 30, kind: "shells" },
    { x: 5360, y: 580, w: 68, h: 30, kind: "shells" },
  ],
  critters: [
    { id: "s1-crab-1", x: 1520, y: GROUND_Y, minX: 1490, maxX: 1560, direction: -1, stunned: 0, color: "#f0705a", speed: 60, species: "crab" },
    { id: "s1-crab-2", x: 3230, y: GROUND_Y, minX: 3160, maxX: 3300, direction: 1, stunned: 0, color: "#ef765d", speed: 64, species: "crab" },
    { id: "s1-puffer", x: 5000, y: GROUND_Y, minX: 4920, maxX: 5110, direction: -1, stunned: 0, color: "#f1bd48", speed: 54, species: "puffer" },
  ],
  springs: [
    { x: 1740, y: 582, strength: 1080, kind: "fizz" },
    { x: 4450, y: 582, strength: 1090, kind: "fizz" },
  ],
  slopes: [],
  secrets: [],
  powerGates: [
    { id: "landing-bubble-route", x: 3298, y: 300, w: 28, h: 105, requires: "bubble", label: "FIZZ" },
  ],
};

const fizzyFallsLevel: LevelDefinition = {
  id: "fizzy-falls",
  name: "Fizzy Falls",
  subtitle: "Bubble lifts and lemonade spray",
  objective: "Ride the fizz geysers and keep above the churning water.",
  worldNumber: 2,
  worldName: "Soda Pop Shore",
  worldLevel: 2,
  worldWidth: 5900,
  worldHeight: WORLD_HEIGHT,
  groundY: GROUND_Y,
  spawnX: 130,
  checkpointX: 2840,
  respawnX: 2890,
  goalX: 5680,
  parTime: 98,
  theme: fizzFalls,
  tide: { lowY: 668, highY: 635, speed: 0.72, phase: 0.7 },
  platforms: [
    ground(-200, 820), ground(990, 700), ground(1870, 720), ground(2780, 760),
    ground(3710, 720), ground(4610, 1490),
    { x: 260, y: 500, w: 210, h: 34, kind: "sand" },
    { id: "fizz-lift-1", x: 650, y: 500, w: 140, h: 30, kind: "bubble", motion: "move-y", range: 72, speed: 1.25 },
    { x: 1030, y: 500, w: 200, h: 36, kind: "coral" },
    { id: "fizz-cap-1", x: 1340, y: 405, w: 150, h: 30, kind: "bottlecap", motion: "move-x", range: 65, speed: 1.2 },
    { x: 1580, y: 500, w: 180, h: 34, kind: "pier" },
    { id: "fizz-lift-2", x: 1725, y: 475, w: 130, h: 30, kind: "bubble", motion: "move-y", range: 65, speed: 1.45, phase: 1.1 },
    { x: 1960, y: 405, w: 200, h: 36, kind: "coral" },
    { id: "fizz-fade-1", x: 2330, y: 500, w: 135, h: 30, kind: "bubble", motion: "vanish", phase: 0.2 },
    { x: 2590, y: 405, w: 170, h: 34, kind: "bottlecap" },
    { x: 2880, y: 500, w: 220, h: 34, kind: "pier" },
    { id: "fizz-lift-3", x: 3280, y: 490, w: 140, h: 30, kind: "bubble", motion: "move-y", range: 75, speed: 1.32, phase: 2.0 },
    { x: 3510, y: 405, w: 180, h: 36, kind: "coral" },
    { x: 3780, y: 500, w: 210, h: 34, kind: "sand" },
    { id: "fizz-cap-2", x: 4140, y: 405, w: 150, h: 30, kind: "bottlecap", motion: "move-x", range: 62, speed: 1.35, phase: 0.8 },
    { id: "fizz-fade-2", x: 4410, y: 500, w: 135, h: 30, kind: "bubble", motion: "vanish", phase: 1.4 },
    { x: 4700, y: 500, w: 220, h: 34, kind: "pier" },
    { x: 5070, y: 405, w: 200, h: 36, kind: "coral" },
    { id: "fizz-lift-4", x: 5350, y: 500, w: 140, h: 30, kind: "bubble", motion: "move-y", range: 62, speed: 1.5 },
  ],
  collectibles: [
    ...fruits("s2-a", 250, 565, 5, 70),
    { id: "s2-pin-1", x: 1110, y: 340, kind: "golden-pin" },
    { id: "s2-berry", x: 1660, y: 565, kind: "fizz-bubble" },
    ...fruits("s2-b", 1020, 565, 5, 78),
    { id: "s2-pin-2", x: 2670, y: 340, kind: "golden-pin" },
    ...fruits("s2-c", 1940, 350, 7, 78),
    { id: "s2-crown", x: 3590, y: 340, kind: "crown-piece" },
    ...fruits("s2-d", 2860, 565, 5, 82),
    { id: "s2-pin-3", x: 5150, y: 340, kind: "golden-pin" },
    ...fruits("s2-e", 4680, 565, 8, 76),
  ],
  hazards: [
    { x: 1235, y: 580, w: 66, h: 30, kind: "shells" },
    { x: 2180, y: 580, w: 70, h: 30, kind: "shells" },
    { x: 3900, y: 580, w: 72, h: 30, kind: "shells" },
    { x: 4930, y: 580, w: 68, h: 30, kind: "shells" },
  ],
  critters: [
    { id: "s2-crab-1", x: 1450, y: GROUND_Y, minX: 1410, maxX: 1510, direction: 1, stunned: 0, color: "#eb6a61", speed: 68, species: "crab" },
    { id: "s2-puffer-1", x: 3030, y: GROUND_Y, minX: 2970, maxX: 3170, direction: -1, stunned: 0, color: "#f0c34f", speed: 58, species: "puffer" },
    { id: "s2-crab-2", x: 4830, y: GROUND_Y, minX: 4760, maxX: 4880, direction: 1, stunned: 0, color: "#ea7565", speed: 70, species: "crab" },
  ],
  springs: [
    { x: 540, y: 582, strength: 1140, kind: "fizz" },
    { x: 1775, y: 582, strength: 1130, kind: "fizz" },
    { x: 3430, y: 582, strength: 1120, kind: "fizz" },
    { x: 5480, y: 582, strength: 1130, kind: "fizz" },
  ],
  slopes: [],
  secrets: [],
};

const taffyTidepools: LevelDefinition = {
  id: "taffy-tidepools",
  name: "Taffy Tidepools",
  subtitle: "A secret beneath the salt-rock shelf",
  objective: "Use Mini Slice to find the tide cave, then time the floating caps.",
  worldNumber: 2,
  worldName: "Soda Pop Shore",
  worldLevel: 3,
  worldWidth: 6100,
  worldHeight: WORLD_HEIGHT,
  groundY: GROUND_Y,
  spawnX: 130,
  checkpointX: 3030,
  respawnX: 3070,
  goalX: 5870,
  parTime: 105,
  theme: tidepool,
  tide: { lowY: 670, highY: 628, speed: 0.5, phase: 1.8 },
  platforms: [
    ground(-200, 1080), ground(1020, 1200), ground(2400, 760), ground(3370, 760),
    ground(4320, 720), ground(5210, 1090),
    { x: 300, y: 500, w: 190, h: 36, kind: "coral" },
    { x: 580, y: 405, w: 190, h: 34, kind: "sand" },
    { id: "tide-cap-1", x: 875, y: 500, w: 130, h: 30, kind: "bottlecap", motion: "move-y", range: 38, speed: 0.95 },
    { x: 1110, y: 500, w: 190, h: 34, kind: "coral" },
    { id: "tide-cave-ceiling", x: 1500, y: 455, w: 520, h: 68, kind: "cave" },
    { x: 1650, y: 355, w: 180, h: 34, kind: "coral" },
    { x: 2040, y: 500, w: 150, h: 34, kind: "sand" },
    { id: "tide-cap-2", x: 2250, y: 485, w: 130, h: 30, kind: "bottlecap", motion: "move-y", range: 40, speed: 0.95, phase: 2.1 },
    { x: 2490, y: 500, w: 200, h: 34, kind: "pier" },
    { x: 2810, y: 405, w: 190, h: 36, kind: "coral" },
    { id: "tide-bubble-1", x: 3170, y: 500, w: 130, h: 30, kind: "bubble", motion: "vanish", phase: 0.8 },
    { x: 3450, y: 500, w: 200, h: 34, kind: "sand" },
    { id: "tide-cap-3", x: 3810, y: 405, w: 145, h: 30, kind: "bottlecap", motion: "move-x", range: 65, speed: 0.9 },
    { id: "tide-bubble-2", x: 4140, y: 500, w: 130, h: 30, kind: "bubble", motion: "vanish", phase: 1.8 },
    { x: 4400, y: 500, w: 200, h: 34, kind: "coral" },
    { x: 4720, y: 405, w: 190, h: 34, kind: "sand" },
    { id: "tide-cap-4", x: 5060, y: 500, w: 130, h: 30, kind: "bottlecap", motion: "move-y", range: 36, speed: 1.0, phase: 0.9 },
    { x: 5330, y: 500, w: 210, h: 34, kind: "pier" },
    { x: 5660, y: 405, w: 185, h: 36, kind: "coral" },
  ],
  collectibles: [
    { id: "s3-mini", x: 360, y: 445, kind: "mini-slice" },
    ...fruits("s3-a", 300, 565, 6, 68),
    { id: "s3-pin-1", x: 670, y: 340, kind: "golden-pin" },
    ...fruits("s3-secret", 1570, 575, 5, 82),
    { id: "s3-crown", x: 1910, y: 575, kind: "crown-piece" },
    { id: "s3-pin-2", x: 1740, y: 300, kind: "golden-pin" },
    ...fruits("s3-b", 2460, 565, 7, 76),
    { id: "s3-berry", x: 2890, y: 345, kind: "fizz-bubble" },
    ...fruits("s3-c", 3420, 565, 7, 80),
    { id: "s3-pin-3", x: 4800, y: 340, kind: "golden-pin" },
    ...fruits("s3-d", 4380, 565, 8, 78),
    ...fruits("s3-e", 5310, 565, 6, 84),
  ],
  hazards: [
    { x: 1270, y: 580, w: 66, h: 30, kind: "shells" },
    { x: 2690, y: 580, w: 72, h: 30, kind: "shells" },
    { x: 3560, y: 580, w: 70, h: 30, kind: "shells" },
    { x: 4510, y: 580, w: 70, h: 30, kind: "shells" },
  ],
  critters: [
    { id: "s3-puffer-1", x: 2110, y: GROUND_Y, minX: 2060, maxX: 2150, direction: 1, stunned: 0, color: "#e7bb4b", speed: 52, species: "puffer" },
    { id: "s3-crab-1", x: 2900, y: GROUND_Y, minX: 2820, maxX: 2960, direction: -1, stunned: 0, color: "#e86d63", speed: 64, species: "crab" },
    { id: "s3-crab-2", x: 5480, y: GROUND_Y, minX: 5420, maxX: 5550, direction: 1, stunned: 0, color: "#e97865", speed: 68, species: "crab" },
  ],
  springs: [
    { x: 1360, y: 582, strength: 1080, kind: "fizz" },
    { x: 4160, y: 582, strength: 1090, kind: "fizz" },
  ],
  slopes: [],
  secrets: [
    { id: "taffy-crown-cave", x: 1500, y: 455, w: 520, h: 155, entrance: "left" },
  ],
};

const boardwalkBounce: LevelDefinition = {
  id: "boardwalk-bounce",
  name: "Boardwalk Bounce",
  subtitle: "Arcade lights above the surf",
  objective: "Chain fizz geysers across the pier before the bubbles pop.",
  worldNumber: 2,
  worldName: "Soda Pop Shore",
  worldLevel: 4,
  worldWidth: 6000,
  worldHeight: WORLD_HEIGHT,
  groundY: GROUND_Y,
  spawnX: 130,
  checkpointX: 2960,
  respawnX: 3010,
  goalX: 5780,
  parTime: 82,
  theme: boardwalk,
  tide: { lowY: 665, highY: 636, speed: 0.86, phase: 0.2 },
  platforms: [
    ground(-200, 850), ground(1040, 730), ground(1950, 700), ground(2830, 760),
    ground(3780, 720), ground(4680, 1520),
    { x: 250, y: 500, w: 220, h: 34, kind: "pier" },
    { id: "boardwalk-fade-1", x: 660, y: 415, w: 135, h: 30, kind: "bubble", motion: "vanish", phase: 0 },
    { x: 850, y: 500, w: 170, h: 34, kind: "bottlecap" },
    { x: 1100, y: 500, w: 210, h: 34, kind: "pier" },
    { id: "boardwalk-crate-1", x: 1430, y: 438, w: 72, h: 72, kind: "breakable" },
    { id: "boardwalk-crate-2", x: 1502, y: 438, w: 72, h: 72, kind: "breakable" },
    { id: "boardwalk-cap-1", x: 1780, y: 405, w: 150, h: 30, kind: "bottlecap", motion: "move-x", range: 68, speed: 1.5 },
    { x: 2020, y: 500, w: 210, h: 34, kind: "pier" },
    { id: "boardwalk-fade-2", x: 2390, y: 405, w: 135, h: 30, kind: "bubble", motion: "vanish", phase: 0.9 },
    { x: 2630, y: 500, w: 170, h: 34, kind: "bottlecap" },
    { x: 2920, y: 500, w: 220, h: 34, kind: "pier" },
    { id: "boardwalk-crate-3", x: 3290, y: 438, w: 72, h: 72, kind: "breakable" },
    { id: "boardwalk-crate-4", x: 3362, y: 438, w: 72, h: 72, kind: "breakable" },
    { id: "boardwalk-cap-2", x: 3600, y: 405, w: 150, h: 30, kind: "bottlecap", motion: "move-x", range: 70, speed: 1.6, phase: 1.1 },
    { x: 3860, y: 500, w: 220, h: 34, kind: "pier" },
    { id: "boardwalk-fade-3", x: 4230, y: 405, w: 135, h: 30, kind: "bubble", motion: "vanish", phase: 1.7 },
    { x: 4460, y: 500, w: 180, h: 34, kind: "bottlecap" },
    { x: 4780, y: 500, w: 220, h: 34, kind: "pier" },
    { id: "boardwalk-cap-3", x: 5180, y: 405, w: 150, h: 30, kind: "bottlecap", motion: "move-y", range: 55, speed: 1.45 },
    { x: 5510, y: 500, w: 220, h: 34, kind: "pier" },
  ],
  collectibles: [
    ...fruits("s4-a", 250, 565, 6, 70),
    { id: "s4-crown-power", x: 720, y: 350, kind: "golden-glow" },
    { id: "s4-pin-1", x: 910, y: 440, kind: "golden-pin" },
    ...fruits("s4-b", 1100, 565, 7, 76),
    { id: "s4-pin-2", x: 2470, y: 340, kind: "golden-pin" },
    { id: "s4-crown", x: 3700, y: 340, kind: "crown-piece" },
    ...fruits("s4-c", 2910, 565, 8, 76),
    { id: "s4-berry", x: 3970, y: 565, kind: "fizz-bubble" },
    ...fruits("s4-d", 3840, 565, 7, 78),
    { id: "s4-pin-3", x: 5400, y: 340, kind: "golden-pin" },
    ...fruits("s4-e", 4780, 565, 8, 76),
  ],
  hazards: [
    { x: 1250, y: 580, w: 68, h: 30, kind: "shells" },
    { x: 2110, y: 580, w: 70, h: 30, kind: "shells" },
    { x: 3060, y: 580, w: 70, h: 30, kind: "shells" },
    { x: 3980, y: 580, w: 72, h: 30, kind: "shells" },
    { x: 4880, y: 580, w: 68, h: 30, kind: "shells" },
  ],
  critters: [
    { id: "s4-crab-1", x: 1620, y: GROUND_Y, minX: 1590, maxX: 1680, direction: 1, stunned: 0, color: "#ed7162", speed: 82, species: "crab" },
    { id: "s4-puffer-1", x: 3220, y: GROUND_Y, minX: 3190, maxX: 3260, direction: -1, stunned: 0, color: "#e8b94a", speed: 68, species: "puffer" },
    { id: "s4-crab-2", x: 5040, y: GROUND_Y, minX: 5000, maxX: 5100, direction: 1, stunned: 0, color: "#e66f61", speed: 84, species: "crab" },
  ],
  springs: [
    { x: 540, y: 582, strength: 1130, kind: "fizz" },
    { x: 1830, y: 582, strength: 1160, kind: "fizz" },
    { x: 3470, y: 582, strength: 1150, kind: "fizz" },
    { x: 5350, y: 582, strength: 1140, kind: "fizz" },
  ],
  slopes: [],
  secrets: [],
  powerGates: [
    { id: "boardwalk-glow-route", x: 3562, y: 285, w: 28, h: 120, requires: "glow", label: "GLOW" },
  ],
};

const lighthousePopOff: LevelDefinition = {
  id: "lighthouse-pop-off",
  name: "Lighthouse Pop-Off",
  subtitle: "A sparkling Soda Pop Shore finale",
  objective: "Catch the runaway soda float and guide it safely to the lighthouse.",
  worldNumber: 2,
  worldName: "Soda Pop Shore",
  worldLevel: 5,
  worldWidth: 6400,
  worldHeight: WORLD_HEIGHT,
  groundY: GROUND_Y,
  spawnX: 130,
  checkpointX: 3160,
  respawnX: 3200,
  goalX: 6160,
  parTime: 90,
  theme: marina,
  tide: { lowY: 666, highY: 634, speed: 0.68, phase: 1.1 },
  cart: { startX: 620, speed: 128, kind: "soda-float" },
  platforms: [
    ground(-200, 1120), ground(1080, 820), ground(2080, 820), ground(3060, 820),
    ground(4040, 800), ground(5000, 1600),
    { x: 330, y: 500, w: 210, h: 34, kind: "pier" },
    { x: 720, y: 405, w: 180, h: 34, kind: "bottlecap" },
    { id: "marina-fade-1", x: 970, y: 500, w: 130, h: 30, kind: "bubble", motion: "vanish", phase: 0.3 },
    { x: 1190, y: 500, w: 220, h: 34, kind: "pier" },
    { id: "marina-cap-1", x: 1610, y: 405, w: 150, h: 30, kind: "bottlecap", motion: "move-x", range: 70, speed: 1.35 },
    { x: 1840, y: 500, w: 190, h: 36, kind: "coral" },
    { x: 2170, y: 500, w: 220, h: 34, kind: "pier" },
    { id: "marina-crate-1", x: 2520, y: 438, w: 72, h: 72, kind: "breakable" },
    { id: "marina-crate-2", x: 2592, y: 438, w: 72, h: 72, kind: "breakable" },
    { id: "marina-fade-2", x: 2860, y: 500, w: 140, h: 30, kind: "bubble", motion: "vanish", phase: 1.2 },
    { x: 3160, y: 500, w: 220, h: 34, kind: "pier" },
    { id: "marina-cap-2", x: 3580, y: 405, w: 150, h: 30, kind: "bottlecap", motion: "move-y", range: 52, speed: 1.4 },
    { x: 3820, y: 500, w: 190, h: 36, kind: "coral" },
    { x: 4140, y: 500, w: 220, h: 34, kind: "pier" },
    { id: "marina-fade-3", x: 4540, y: 405, w: 140, h: 30, kind: "bubble", motion: "vanish", phase: 2.0 },
    { x: 4780, y: 500, w: 180, h: 34, kind: "bottlecap" },
    { x: 5090, y: 500, w: 220, h: 34, kind: "pier" },
    { id: "marina-cap-3", x: 5480, y: 405, w: 150, h: 30, kind: "bottlecap", motion: "move-x", range: 65, speed: 1.5, phase: 0.6 },
    { x: 5780, y: 500, w: 220, h: 34, kind: "pier" },
  ],
  collectibles: [
    ...fruits("s5-a", 300, 565, 7, 58),
    { id: "s5-pin-1", x: 800, y: 340, kind: "golden-pin" },
    { id: "s5-berry", x: 1290, y: 565, kind: "fizz-bubble" },
    ...fruits("s5-b", 1160, 565, 7, 72),
    { id: "s5-pin-2", x: 2250, y: 565, kind: "golden-pin" },
    { id: "s5-crown", x: 3900, y: 340, kind: "crown-piece" },
    ...fruits("s5-c", 3150, 565, 8, 76),
    { id: "s5-shield", x: 3520, y: 565, kind: "pin-shield" },
    { id: "s5-crown-power", x: 4210, y: 565, kind: "golden-glow" },
    ...fruits("s5-d", 4120, 565, 8, 78),
    { id: "s5-pin-3", x: 5560, y: 340, kind: "golden-pin" },
    ...fruits("s5-e", 5080, 565, 9, 76),
  ],
  hazards: [
    { x: 920, y: 580, w: 70, h: 30, kind: "shells" },
    { x: 1780, y: 580, w: 72, h: 30, kind: "shells" },
    { x: 2760, y: 580, w: 72, h: 30, kind: "shells" },
    { x: 3740, y: 580, w: 72, h: 30, kind: "shells" },
    { x: 4700, y: 580, w: 72, h: 30, kind: "shells" },
  ],
  critters: [
    { id: "s5-crab-1", x: 1510, y: GROUND_Y, minX: 1450, maxX: 1580, direction: 1, stunned: 0, color: "#e76f63", speed: 72, species: "crab" },
    { id: "s5-puffer-1", x: 2350, y: GROUND_Y, minX: 2310, maxX: 2440, direction: -1, stunned: 0, color: "#e9b94c", speed: 62, species: "puffer" },
    { id: "s5-crab-2", x: 4260, y: GROUND_Y, minX: 4210, maxX: 4350, direction: 1, stunned: 0, color: "#eb7464", speed: 76, species: "crab" },
    { id: "s5-puffer-2", x: 5280, y: GROUND_Y, minX: 5240, maxX: 5360, direction: -1, stunned: 0, color: "#e5b649", speed: 66, species: "puffer" },
  ],
  springs: [
    { x: 1020, y: 582, strength: 1110, kind: "fizz" },
    { x: 1940, y: 582, strength: 1120, kind: "fizz" },
    { x: 4800, y: 582, strength: 1110, kind: "fizz" },
  ],
  slopes: [],
  secrets: [],
  powerGates: [
    { id: "marina-shield-route", x: 3775, y: 380, w: 28, h: 120, requires: "shield", label: "SHIELD" },
  ],
};

export const CROWN_RUSH: LevelDefinition = {
  id: "crown-rush",
  name: "Crown Rush",
  subtitle: "The Crown Vault time trial",
  objective: "Chain every power, collect three Golden Pins and reach the vault bell before par.",
  worldNumber: 0,
  worldName: "Crown Vault",
  worldLevel: 1,
  worldWidth: 4200,
  worldHeight: WORLD_HEIGHT,
  groundY: GROUND_Y,
  spawnX: 130,
  checkpointX: 2180,
  respawnX: 2160,
  goalX: 4050,
  parTime: 58,
  theme: crownVault,
  bonus: true,
  unlockCrowns: 5,
  platforms: [
    ground(-200, 900), ground(780, 520), ground(1410, 520),
    ground(2070, 500), ground(2730, 500), ground(3400, 1000),
    { x: 360, y: 475, w: 170, h: 34, kind: "log" },
    { x: 620, y: 375, w: 150, h: 36, kind: "bottlecap" },
    { x: 900, y: 475, w: 170, h: 34, kind: "pier" },
    { x: 1180, y: 390, w: 150, h: 36, kind: "leaf" },
    { x: 1510, y: 500, w: 200, h: 34, kind: "sand" },
    { x: 1830, y: 405, w: 180, h: 36, kind: "coral" },
    { x: 2200, y: 500, w: 210, h: 34, kind: "log" },
    { id: "vault-lift", x: 2500, y: 420, w: 140, h: 30, kind: "bubble", motion: "move-y", range: 65, speed: 1.55 },
    { x: 2820, y: 405, w: 180, h: 36, kind: "stone" },
    { x: 3150, y: 500, w: 170, h: 34, kind: "pier" },
    { x: 3500, y: 405, w: 190, h: 36, kind: "leaf" },
    { x: 3820, y: 500, w: 180, h: 34, kind: "bottlecap" },
  ],
  collectibles: [
    ...fruits("vault-a", 250, 565, 5, 62),
    { id: "vault-shield", x: 560, y: 565, kind: "pin-shield" },
    { id: "vault-pin-1", x: 695, y: 315, kind: "golden-pin" },
    ...fruits("vault-b", 940, 420, 4, 72),
    { id: "vault-bubble", x: 1500, y: 565, kind: "fizz-bubble" },
    { id: "vault-pin-2", x: 1910, y: 345, kind: "golden-pin" },
    ...fruits("vault-c", 2180, 565, 5, 76),
    { id: "vault-glow", x: 2790, y: 565, kind: "golden-glow" },
    ...fruits("vault-d", 2835, 345, 4, 72),
    { id: "vault-pin-3", x: 3590, y: 345, kind: "golden-pin" },
    ...fruits("vault-e", 3740, 565, 4, 72),
  ],
  hazards: [
    { x: 800, y: 580, w: 60, h: 30, kind: "shells" },
    { x: 1760, y: 580, w: 64, h: 30, kind: "bramble" },
    { x: 2840, y: 580, w: 68, h: 30, kind: "shells" },
    { x: 3920, y: 580, w: 64, h: 30, kind: "bramble" },
  ],
  critters: [
    { id: "vault-bud", x: 1080, y: GROUND_Y, minX: 1010, maxX: 1160, direction: 1, stunned: 0, speed: 76 },
    { id: "vault-crab", x: 2340, y: GROUND_Y, minX: 2280, maxX: 2410, direction: -1, stunned: 0, speed: 82, species: "crab" },
    { id: "vault-puffer", x: 3650, y: GROUND_Y, minX: 3580, maxX: 3740, direction: 1, stunned: 0, speed: 72, species: "puffer" },
  ],
  springs: [
    { x: 1260, y: 582, strength: 1120 },
    { x: 2530, y: 582, strength: 1140, kind: "fizz" },
    { x: 3230, y: 582, strength: 1130 },
  ],
  slopes: [],
  secrets: [],
  powerGates: [
    { id: "vault-bubble-route", x: 1778, y: 285, w: 28, h: 120, requires: "bubble", label: "FIZZ" },
    { id: "vault-glow-route", x: 3455, y: 285, w: 28, h: 120, requires: "glow", label: "GLOW" },
  ],
};

export const WORLDS: readonly WorldDefinition[] = [
  { number: 1, id: "sunny-grove", name: "Sunny Grove", subtitle: "Five trails through the golden canopy" },
  { number: 2, id: "soda-pop-shore", name: "Soda Pop Shore", subtitle: "Five sparkling adventures beside the fizzing sea" },
];

export const LEVELS: readonly LevelDefinition[] = [
  groveGateway,
  burrowAndBloom,
  treetopPicnic,
  orchardRush,
  fruitCartChase,
  lemonadeLanding,
  fizzyFallsLevel,
  taffyTidepools,
  boardwalkBounce,
  lighthousePopOff,
];

export const ALL_LEVELS: readonly LevelDefinition[] = [...LEVELS, CROWN_RUSH];

export function freshCollectibles(level: LevelDefinition): Collectible[] {
  return level.collectibles.map((item) => ({ ...item, collected: false }));
}

export function freshCritters(level: LevelDefinition): GroveCritter[] {
  return level.critters.map((critter) => ({ ...critter, stunned: 0, defeated: false, defeatTimer: 0 }));
}
